import { clone, merge, isString } from 'lodash';
import { TFunction } from 'react-i18next';

import { ActionId } from 'admin/actions/Actions';
import { FieldName as CredentialFieldname } from 'admin/credentials/Credentials';
import {
    GenerateSuggestedResponseRequest,
    OriginService,
    PromptDataType,
    generateResponse,
} from 'admin/recommends/Recommends';
import { hashtagSearchUrl, SourceId } from 'admin/sources/Sources';
import { isAuthorized, Profile } from 'auth/Auth';
import { Comment, Mention } from 'common/comments2/Comments2.types';
import { Config } from 'core/util/Config';
import { allowHealthgradesApiResponse } from 'reviews2/feed/review-yeti/reply/ReviewReply.util';
import { ReviewProps, ReviewMode, ReadOnlyReason } from 'reviews2/feed/review-yeti/Review.types';
import { RespondableType, ReviewSentimentToCommon } from 'reviews2/feed/Reviews.types';
import { getReview3 } from 'reviews2/Reviews.api';
import { LOCAL_STORAGE_CONFIG_KEY } from 'social/publisher/Publisher';
import { PostOrigin, PublisherConfig } from 'social/publisher/Publisher.types';
import { Ticket } from 'tickets/actions2/common/action.types';

const SOCIAL_PUBLISHER_KEY = LOCAL_STORAGE_CONFIG_KEY;

export const createContext = async (
    defaults: Partial<ReviewProps>,
    handlers: Partial<ReviewProps['handlers']>,
    options: Partial<ReviewProps['options']>,
    review?: any,
    reviewId?: string,
    profile?: Profile,
    config?: Config,
    replies?: Comment[],
    actions?: Record<string, boolean>,
    mode?: ReviewMode,
    rid?: string,
    rrid?: string,
    t?: TFunction<'reviews2'>,
    ticketCreated?: Ticket | undefined,
): Promise<{ context: ReviewProps | null; error?: string }> => {
    const contextStartTime = performance.now();
    let context: ReviewProps = {
        replies,
        profile,
        config,
        actions,
        handlers: { ...defaults.handlers, ...handlers },
        rid,
        mode,
        rrid,
        reviewTicket: ticketCreated,
    };
    const args = {
        includeLabels: true,
        includeSource: true,
        includeTenant: true,
        includeLocation: true,
        includeResponses: true,
        includeCredentials: true,
        includeAttributionUsers: true,
        includeRecommendedReplies: isString(rrid),
        includeCredentialStatus: true,
    };
    const clonedOptions = clone(options);

    if (review) {
        context = { ...context, review };
    } else if (reviewId) {
        try {
            const ticketDate = ticketCreated?.reviewDate ?? ticketCreated?.createdDate;
            const reviewDateIso = ticketDate != null ? new Date(ticketDate).toISOString() : undefined;

            const startTime = performance.now();
            const { review, responses } = await getReview3(profile?.tenant?.id!, reviewId, {
                ...args,
                agencyId: ticketCreated ? ticketCreated.agencyID : undefined,
                reviewDate: reviewDateIso,
            });
            const elapsed = Math.round(performance.now() - startTime);

            console.info(`[ReviewYeti] getReview3 reviewId=${reviewId} took ${elapsed}ms`);

            review.replies = responses;
            context = { ...context, review };
        } catch (error: any) {
            return {
                context: null,
                error: error.response?.data?.message || error.message || t?.('FAILED_TO_LOAD_REVIEW_DATA'),
            };
        }
    } else {
        return { context };
    }

    if (clonedOptions?.readOnly === undefined && t && context.review) {
        let readOnly = context.review.respondable_type?.includes(RespondableType.NotRespondable);
        let readOnlyText = t('REPLYING_DISABLED_NOT_RESPONDABLE');
        let readOnlyReason = ReadOnlyReason.NotRespondable;

        // if source supports replying to reviews
        if (!readOnly) {
            // check for credential and set readOnly
            readOnly = context.review.credentialStatus
                ? context.review.credentialStatus.status !== CredentialFieldname.Valid
                : true;

            if (readOnly && context.review.source?.id === SourceId.Yelp) {
                readOnlyText = t('REPLYING_DISABLED_YELP');
                readOnlyReason = ReadOnlyReason.NotRespondable;
            } else {
                if (!context.review.source?.requireCredentials && context.review.source?.id !== SourceId.Yelp) {
                    readOnly = false;
                }

                /* Check if HealthGrades API response is allowed */
                if (readOnly && allowHealthgradesApiResponse(context.review)) {
                    readOnly = false;
                }

                // if review's location doesn't have valid, attached credentials
                if (readOnly) {
                    // display as such in the response editor
                    if (context.review?.review_source_id === SourceId.ApartmentRatings) {
                        readOnlyText = t('REPLYING_DISABLED_CREDENTIALS_APARTMENT_RATINGS');
                    } else {
                        readOnlyText = t('REPLYING_DISABLED_CREDENTIALS');
                    }
                    readOnlyReason = ReadOnlyReason.Credentials;
                } else {
                    // check if the current user has req'd permission
                    readOnly = !isAuthorized({ actions: [ActionId.ReviewResponses.Add] });

                    if (readOnly) {
                        readOnlyText = t('REPLYING_DISABLED');
                        readOnlyReason = ReadOnlyReason.Auth;
                    }
                }
            }
        }

        /* eslint-disable */
        clonedOptions!.readOnly = readOnly;
        clonedOptions!.readOnlyReason = readOnlyReason;

        if (clonedOptions!.readOnly) {
            clonedOptions!.editorOptions = {
                ...clonedOptions!.editorOptions,
                readOnlyText,
            };
        }
        /* eslint-enable */
    }

    context = {
        ...context,
        options: merge(
            {},
            clone(
                merge({}, defaults.options, {
                    editorOptions: {
                        responseGeneratorOptions: {
                            enable:
                                !options?.readOnly && isAuthorized({ actions: [ActionId.ReviewResponses.AIResponses] }),
                            params: {
                                promptText:
                                    context.review.raw_body_text ||
                                    context.review.properties?.reduce((acc: string, prop: Record<string, any>) => {
                                        if (prop.key && prop.value?.length) {
                                            // eslint-disable-next-line
                                            acc += `${prop.key}: ${prop.value[0]} `;
                                        }
                                        return acc;
                                    }, ''),
                                sentiment: ReviewSentimentToCommon[context.review!.taxonomy_overall_sentiment],
                                promptDataType: PromptDataType.Review,
                                originService: OriginService.ReviewCore,
                                originID: context.review!.document_id,
                            } as GenerateSuggestedResponseRequest,
                            api: (params: GenerateSuggestedResponseRequest) => {
                                return generateResponse(context.review!.r4e_tenant_id, params);
                            },
                        },
                    },
                    showEditor: true,
                }),
            ),
            { locale: profile?.user.locale },
            clonedOptions,
        ),
    };

    const totalElapsed = Math.round(performance.now() - contextStartTime);

    console.info(`[ReviewYeti] createContext reviewId=${reviewId} total=${totalElapsed}ms`);

    return { context };
};

export const hashTagParser = (text = '', sourceId: string): string => {
    const url = hashtagSearchUrl[sourceId];

    if (url && text) {
        return text.replace(/(^|\s)#([a-z\d-]+)/gi, `$1<a href="${url}$2" target="_blank">#$2</a>`);
    }

    return text;
};

export const mentionParser = (text = '', mentions?: Mention[]): string => {
    if (mentions) {
        mentions.forEach((mention: any) => {
            if (mention.permalink) {
                const hyper = text.substring(mention.offset, mention.offset + mention.length);

                // eslint-disable-next-line
                text = `${text.substring(0, mention.offset)}<a class="parsed-mention" href="${
                    mention.permalink
                }" target="_blank">${hyper}</a>${text.substring(mention.offset + mention.length)}`;
            }
        });
    }

    return text;
};

export const setPublisherConfig = (review: Comment, l10nText: string, returnUrl: string): void => {
    const url = review.properties?.permalink?.stringValues?.[0];
    const config: PublisherConfig = {
        publishType: 'PUBLISH_NOW',
        returnUrl,
        editorMode: 'POST_PUBLISH',
        media: [],
        content: [
            {
                text: l10nText,
                tags: [],
                attributes: {},
            },
        ],
        selectedLocations: [review.location?.uid!],
        isLocationsLocked: true,
        postType: 'LINK',
        postOrigin: PostOrigin.REVIEW,
    };

    if (url) {
        config.content?.push({
            text: url!,
            tags: ['link'],
            attributes: {
                link: url,
            },
        });
    }

    localStorage.setItem(SOCIAL_PUBLISHER_KEY, JSON.stringify(config));
};

export function stripFunctions<T>(input: T): T {
    if (typeof input === 'function') {
        return undefined as unknown as T;
    }

    if (Array.isArray(input)) {
        return input.map(stripFunctions).filter((item) => item !== undefined) as unknown as T;
    }

    if (input !== null && typeof input === 'object') {
        return Object.entries(input).reduce((acc, [key, value]) => {
            const cleaned = stripFunctions(value);

            if (cleaned !== undefined) {
                (acc as Record<string, unknown>)[key] = cleaned;
            }
            return acc;
        }, {} as Partial<Record<keyof T, unknown>>) as T;
    }

    return input;
}
